/**
 * Report numbers. Written by hand instead of `Intl`: in `es` a four-digit
 * integer comes out without a separator ("1234"), and the approved mockup
 * always groups thousands.
 */
const separators = (language: string) =>
  language.startsWith('es')
    ? { group: '.', decimal: ',' }
    : { group: ',', decimal: '.' };

export const formatInt = (value: number, language: string): string =>
  String(Math.round(value)).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    separators(language).group
  );

/** One decimal, with the language separator. */
export const formatDecimal = (value: number, language: string): string =>
  value.toFixed(1).replace('.', separators(language).decimal);

/**
 * Whole-number shares that add up to 100 (largest remainder): each value gets
 * its floored share and the leftover points go to the largest remainders, ties
 * by position. All zeros when there is nothing to share.
 */
export const roundedPercentages = (values: number[]): number[] => {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total <= 0) return values.map(() => 0);

  const exact = values.map((v) => (v / total) * 100);
  const result = exact.map(Math.floor);
  let leftover = 100 - result.reduce((sum, v) => sum + v, 0);

  exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
    .forEach(({ index }) => {
      if (leftover > 0) {
        result[index] += 1;
        leftover -= 1;
      }
    });

  return result;
};

/** Joins the non-empty parts, so an absent one never leaves a dangling dot. */
export const joinParts = (
  parts: (string | null | undefined)[],
  separator = ' · '
): string =>
  parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(separator);

/** Zone name for display; the backend allows `''`, never show the raw id. */
export const zoneLabel = (
  zone: { id?: string; name?: string | null },
  fallback: string
): string => zone.name?.trim() || fallback;
